// ABOUTME: Public page for players to join a game via invite link.
// ABOUTME: Shows a name dialog on first visit; navigates to score card when game starts.
import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAuth } from '@clerk/react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { InputDialog } from '../components/InputDialog';
import { useGamePlayer } from '../hooks/useGamePlayer';
import { useGameRoom } from '../hooks/useGameRoom';
import {
  gameActionsControllerJoinGame,
  gameActionsControllerGetGame,
  gamePlayerActionsControllerGetMyGamePlayer,
} from '../api/generated';
import type { GameDto } from '../api/generated';
import { ApiError } from '../api/client';
import { trackGameJoined, trackJoinPageViewed } from '../analytics/events';

const PLAYABLE_STATUSES = ['OPEN', 'ACTIVE'];

export function JoinGamePage() {
  const { id: gameId } = useParams<{ id: string }>();
  const { gamePlayerId, setGamePlayerId } = useGamePlayer(gameId!);
  const { isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tierLimit, setTierLimit] = useState(false);
  const pendingNameRef = useRef<string>('');

  useEffect(() => {
    trackJoinPageViewed(gameId!);
  }, [gameId]);

  // For logged-in users, validate the cached gamePlayerId against the server.
  // This corrects stale localStorage entries from previous games with the same short ID.
  useEffect(() => {
    if (!isSignedIn) return;
    async function validateMyPlayer() {
      try {
        const token = await getToken();
        const res = await gamePlayerActionsControllerGetMyGamePlayer(
          gameId!,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.status === 200) {
          setGamePlayerId(res.data.id);
        } else {
          // Not in this game — clear any stale entry so the join form shows.
          setGamePlayerId('');
        }
      } catch {
        // Network errors: leave localStorage as-is and let the user proceed normally.
      }
    }
    validateMyPlayer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, isSignedIn]);

  // Navigate all players to their score card when the game is playable.
  const goToScoreCard = useCallback(() => {
    navigate(`/games/${gameId}/play`);
  }, [gameId, navigate]);

  // Check game status and navigate if already playable.
  // Used on mount (to handle arriving at an already-started game) and on
  // WebSocket reconnect (to handle the game starting while the tab was dormant).
  const checkAndRedirect = useCallback(async () => {
    if (!gamePlayerId) return;
    try {
      const res = await gameActionsControllerGetGame(gameId!);
      if (res.status === 200 && PLAYABLE_STATUSES.includes(res.data.status)) {
        goToScoreCard();
      }
    } catch {
      // Network errors: will retry on the next reconnect.
    }
  }, [gameId, gamePlayerId, goToScoreCard]);

  useEffect(() => {
    checkAndRedirect();
  }, [checkAndRedirect]);

  const attemptJoin = useCallback(async (name: string) => {
    try {
      const token = isSignedIn ? await getToken() : null;
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await gameActionsControllerJoinGame(
        gameId!,
        { firstName: name },
        { headers },
      );
      if (response.status === 201) {
        setGamePlayerId(response.data.id);
        trackGameJoined(gameId!);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setTierLimit(true);
      } else {
        throw err;
      }
    }
  }, [gameId, setGamePlayerId, isSignedIn, getToken]);

  // Listen for real-time game status changes.
  const handleEvent = useCallback((event: string, data: unknown) => {
    if (event === 'game.updated') {
      const { game } = data as { game: GameDto };
      if (PLAYABLE_STATUSES.includes(game.status)) {
        goToScoreCard();
      }
    }
    if (event === 'tier.upgraded' && tierLimit) {
      setTierLimit(false);
      attemptJoin(pendingNameRef.current).catch(() => {
        setError('Unable to join the game. Please try again.');
      });
    }
  }, [goToScoreCard, tierLimit, attemptJoin]);

  useGameRoom({ gameId: gameId!, onEvent: handleEvent, onReconnect: checkAndRedirect });

  const handleJoin = async () => {
    if (!firstName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      pendingNameRef.current = firstName.trim();
      await attemptJoin(firstName.trim());
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tierLimit) {
    return (
      <Container maxWidth="sm">
        <Box mt={8} textAlign="center">
          <Typography variant="h5" gutterBottom>
            This game is full
          </Typography>
          <Typography color="text.secondary">
            The host needs to upgrade to allow more players to join.
            You'll be added automatically once they do.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (gamePlayerId) {
    return (
      <Stack spacing={2} alignItems="center" pt={8}>
        <Typography variant="h5">You've joined!</Typography>
        <Typography variant="body1">
          Waiting for the game to start. Keep this page open!
        </Typography>
      </Stack>
    );
  }

  return (
    <InputDialog
      open
      title="Join Game"
      label="Your Name"
      value={firstName}
      onChange={setFirstName}
      onSubmit={handleJoin}
      error={error}
      disabled={loading}
      disableEscapeKeyDown
    >
      <Button
        variant="contained"
        onClick={handleJoin}
        disabled={loading || !firstName.trim()}
      >
        {loading ? <CircularProgress size={20} /> : 'Join Game'}
      </Button>
    </InputDialog>
  );
}
